import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Users,
  Download,
  ChevronDown,
  Plus,
  Eye,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import SubmissionBreadcrumbs from './components/SubmissionBreadcrumbs';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import ScopedSubmissionsTable from './components/tabs/ScopedSubmissionsTable';
import { useSubmission } from '@/hooks/submissions/useSubmission';
import BulkInviteUsersModal from './components/BulkInviteUsersModal';
import UserManagement from './components/tabs/UserManagement';
import Activity from './components/tabs/Activity';
import SubmissionDetailPanel from './components/SubmissionDetailPanel';
import { shouldShowSubmissionDetailPanel } from './utils/shouldShowSubmissionDetailPanel';
import Documents from './components/pizza_tracker_tabs/Documents';
import FinalDocuments from './components/pizza_tracker_tabs/FinalDocuments';
import PreFormWizard from '@/features/submission_forms/PreFormWizard';

interface SubmissionShowLoadedProps {
  submissionId: string;
}

const SubmissionShowLoaded: React.FC<SubmissionShowLoadedProps> = ({
  submissionId,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { submission, isLoading, error } = useSubmission(submissionId!);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [usersRefreshTrigger, setUsersRefreshTrigger] = useState(0);

  const handleDownload = useCallback(() => {
    window.open(`/submissions/${submissionId}/form?view=print`, '_blank');
  }, [submissionId]);

  const handleDownloadBlank = useCallback(() => {
    window.open(
      `/submissions/${submissionId}/form?view=print&print_template=true`,
      '_blank'
    );
  }, [submissionId]);

  const handleInviteUsers = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
  }, []);

  const handleInvitationSuccess = useCallback(() => {
    setTimeout(() => {
      setUsersRefreshTrigger(prev => prev + 1);
    }, 500);
  }, []);

  const handleViewForm = useCallback(() => {
    const canEditForm = submission?.can_edit && submission?.editable;
    const path = canEditForm
      ? `/submissions/${submissionId}/form?mode=edit`
      : `/submissions/${submissionId}/form`;
    navigate(path);
  }, [navigate, submissionId, submission?.can_edit, submission?.editable]);

  useEffect(() => {
    if (submission) {
      const isStudyOrSite =
        submission.submission_type === 'Study' ||
        submission.submission_type === 'StudySite';
      const title = isStudyOrSite
        ? submission.study?.protocol_number ||
          submission.protocol_number ||
          submission.title ||
          'Submission Details'
        : submission.protocol_number ||
          submission.title ||
          'Submission Details';

      document.title = `${title} - OneVerse`;
    }

    return () => {
      document.title = 'OneVerse';
    };
  }, [submission, submissionId]);

  const canManageUsers = submission?.can_manage_users ?? false;
  const canInviteUsers = submission?.can_invite_users ?? false;
  const hasDetailPanel = submission
    ? shouldShowSubmissionDetailPanel(submission)
    : false;

  // Whose users the Users tab lists — the submission itself, or (for a "New
  // Site/Investigator" submission that has created its owner StudySite) the site —
  // and whether the viewer may edit that list. Resolved server-side by
  // SubmissionUserPolicy#users_tab.
  const usersResourcePath =
    submission?.users_tab?.path ?? submission?.show_path;
  const canManageListedUsers =
    submission?.users_tab?.manageable ?? canManageUsers;
  // The Invite Users modal always targets the submission: the backend offers the
  // site roles too for a "New Site/Investigator" submission and grants each
  // invite on the right resource (Invitations::RoleTargets).
  const assignableRolesPath = submission?.show_path ?? '';
  const bulkInvitationsPath = assignableRolesPath
    ? `${assignableRolesPath}/bulk_invitations`
    : '';

  const tabs = useMemo(
    () => [
      { id: 'activity', label: 'Activity' },
      { id: 'documents', label: 'Submitted Documents' },
      { id: 'final-documents', label: 'Final Documents' },
      ...(canManageUsers ? [{ id: 'users', label: 'Users' }] : []),
    ],
    [canManageUsers]
  );

  const tabFromUrl = searchParams.get('tab');
  const validTabIds = tabs.map(tab => tab.id);
  const defaultTab = 'activity';
  const activeTab =
    tabFromUrl && validTabIds.includes(tabFromUrl) ? tabFromUrl : defaultTab;

  const handleTabChange = useCallback(
    (tabId: string) => {
      setSearchParams({ tab: tabId });
    },
    [setSearchParams]
  );

  const renderTabContent = useCallback(() => {
    if (!submission) return null;

    if (activeTab === 'activity') {
      return <Activity submissionId={submission.id} />;
    }

    if (activeTab === 'users' && canManageUsers) {
      return (
        <UserManagement
          submissionId={submission.id}
          resourcePath={usersResourcePath}
          canManageUsers={canManageListedUsers}
          refreshTrigger={usersRefreshTrigger}
          exportFilename={`submission-${submission.display_id ?? submission.id}-users.csv`}
        />
      );
    }

    if (activeTab === 'submissions') {
      return (
        <ScopedSubmissionsTable
          ownerId={submission.id}
          className="bg-white rounded-lg border border-gray-200"
        />
      );
    }

    if (activeTab === 'documents') {
      return <Documents submissionId={submission.id} compact={false} />;
    }

    if (activeTab === 'final-documents') {
      return <FinalDocuments submissionId={submission.id} />;
    }

    return null;
  }, [
    activeTab,
    submission,
    canManageUsers,
    canManageListedUsers,
    usersRefreshTrigger,
    usersResourcePath,
  ]);

  const pageTitle =
    submission?.display_id || submission?.title || 'Submission Details';

  if (isLoading) {
    return (
      <div className="bg-white h-screen flex flex-col overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-8 w-64" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-40" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="mx-auto max-w-md">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {error || 'Submission not found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              The submission you&apos;re looking for could not be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = submission.can_edit && submission.editable;
  const isDraft = submission.status === 'draft';
  const hasForm =
    !!submission.submission_template_id ||
    (submission.question_tree?.length ?? 0) > 0;

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden">
      {/* Page Header */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <SubmissionBreadcrumbs submission={submission} />
            <h1 className="text-gray-900">
              <span className="font-semibold text-2xl">{pageTitle}</span>
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {canInviteUsers && (
              <button
                onClick={handleInviteUsers}
                className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <Users className="w-4 h-4" />
                <span>Invite Users</span>
              </button>
            )}

            {(isDraft || hasForm) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isDraft && (
                    <DropdownMenuItem
                      onSelect={handleDownload}
                      className="cursor-pointer"
                    >
                      Current
                    </DropdownMenuItem>
                  )}
                  {hasForm && (
                    <DropdownMenuItem
                      onSelect={handleDownloadBlank}
                      className="cursor-pointer"
                    >
                      Blank Form
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {hasForm &&
              (canEdit ? (
                <button
                  onClick={handleViewForm}
                  className="flex items-center gap-2 bg-primary hover:bg-primary-600 text-white text-sm font-medium px-3 py-1.5 rounded-md transition-colors cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Complete Submission</span>
                </button>
              ) : (
                <button
                  onClick={handleViewForm}
                  className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Form</span>
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex gap-0 flex-1 overflow-hidden">
        {/* Left: scrollable tab area */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200 pt-6 px-8 flex-shrink-0">
            <div className="flex gap-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-8 pt-8 pb-8">{renderTabContent()}</div>
          </div>
        </div>

        {/* Right: detail panel */}
        {hasDetailPanel && <SubmissionDetailPanel submission={submission} />}
      </div>

      <BulkInviteUsersModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        bulkInvitationsPath={bulkInvitationsPath}
        assignableRolesPath={assignableRolesPath}
        onSuccess={handleInvitationSuccess}
      />
    </div>
  );
};

const SubmissionShow: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();

  if (submissionId === 'new') {
    return <PreFormWizard />;
  }

  return <SubmissionShowLoaded submissionId={submissionId!} />;
};

export default SubmissionShow;
